/**
 * Validation Service
 * Handles local validation logic for audit scanning
 */
import { normalizeScanToken } from '../constants/scan-token.constants';
function getScannedExpectedAnimalIds(scannedAnimals) {
    return new Set(scannedAnimals
        .filter((animal) => animal.isExpected && animal.animalId)
        .map((animal) => animal.animalId));
}
/**
 * Match scan token against expected animals by RapID barcode or RFID tag
 */
export function matchScanToken(token, expectedAnimals) {
    const normalizedToken = normalizeScanToken(token) ?? token.trim().toUpperCase();
    return expectedAnimals.find((animal) => animal.rapidTagBarcode === normalizedToken ||
        animal.rfidTagId?.trim().toUpperCase() === normalizedToken);
}
/**
 * Match barcode against expected animals
 */
export function matchBarcode(barcode, expectedAnimals) {
    return matchScanToken(barcode, expectedAnimals);
}
/**
 * Check if animal has already been scanned
 */
export function isDuplicate(token, scannedAnimals, expectedAnimals = []) {
    const normalizedToken = normalizeScanToken(token) ?? token.trim().toUpperCase();
    const isTokenDuplicate = scannedAnimals.some((animal) => animal.rapidTagBarcode.trim().toUpperCase() === normalizedToken);
    if (isTokenDuplicate) {
        return true;
    }
    const expectedAnimal = matchScanToken(normalizedToken, expectedAnimals);
    if (expectedAnimal) {
        const scannedExpectedAnimalIds = getScannedExpectedAnimalIds(scannedAnimals);
        return scannedExpectedAnimalIds.has(expectedAnimal.id);
    }
    return scannedAnimals.some((animal) => animal.rapidTagBarcode === normalizedToken);
}
/**
 * Validate scanned animal
 */
export function validateScannedAnimal(token, expectedAnimals, scannedAnimals) {
    const normalizedToken = normalizeScanToken(token) ?? token.trim().toUpperCase();
    // Check for duplicate
    if (isDuplicate(normalizedToken, scannedAnimals, expectedAnimals)) {
        return {
            isValid: false,
            isExpected: false,
            isDuplicate: true,
            error: `Scan ${normalizedToken} has already been scanned.`,
        };
    }
    // Check if expected
    const expectedAnimal = matchScanToken(normalizedToken, expectedAnimals);
    const isExpected = !!expectedAnimal;
    return {
        isValid: true,
        isExpected,
        isDuplicate: false,
        expectedAnimal,
    };
}
/**
 * Get missing expected animals
 */
export function getMissingAnimals(expectedAnimals, scannedAnimals) {
    const scannedAnimalIds = getScannedExpectedAnimalIds(scannedAnimals);
    return expectedAnimals.filter((animal) => !scannedAnimalIds.has(animal.id));
}
/**
 * Get unexpected animals (scanned but not expected)
 */
export function getUnexpectedAnimals(scannedAnimals) {
    return scannedAnimals.filter((animal) => !animal.isExpected);
}
/**
 * Check if all expected animals have been scanned
 */
export function areAllExpectedScanned(expectedAnimals, scannedAnimals) {
    if (expectedAnimals.length === 0) {
        return false;
    }
    const scannedAnimalIds = getScannedExpectedAnimalIds(scannedAnimals);
    return expectedAnimals.every((animal) => scannedAnimalIds.has(animal.id));
}
/**
 * Check if there are any exceptions (missing or unexpected animals)
 */
export function hasExceptions(expectedAnimals, scannedAnimals) {
    const missing = getMissingAnimals(expectedAnimals, scannedAnimals);
    const unexpected = getUnexpectedAnimals(scannedAnimals);
    return missing.length > 0 || unexpected.length > 0;
}
/**
 * Get exception summary
 */
export function getExceptionSummary(expectedAnimals, scannedAnimals) {
    const missingAnimals = getMissingAnimals(expectedAnimals, scannedAnimals);
    const unexpectedAnimals = getUnexpectedAnimals(scannedAnimals);
    return {
        missingAnimals,
        unexpectedAnimals,
        hasExceptions: missingAnimals.length > 0 || unexpectedAnimals.length > 0,
    };
}
//# sourceMappingURL=validation.service.js.map